<template>
  <div class="bank-account">
    <div class="section-header">
      <h3>🏦 اطلاعات حساب بانکی</h3>
      <span v-if="hasRecord" :class="['status-badge', statusClass]">{{ statusLabel }}</span>
      <span v-else class="status-badge status-empty">ثبت نشده</span>
    </div>

    <!-- ===== پیام‌های وضعیت ===== -->
    <div v-if="!hasRecord" class="info-box info-blue">
      <p>📝 هنوز اطلاعات بانکی خود را ثبت نکرده‌اید. لطفاً فرم زیر را تکمیل کنید.</p>
    </div>

    <div v-else-if="form.status === 'pending'" class="info-box info-orange">
      <p>⏳ اطلاعات بانکی شما در انتظار تایید مدیریت است. پس از تایید، امکان ویرایش وجود ندارد.</p>
    </div>

    <div v-else-if="form.status === 'approved'" class="info-box info-green">
      <p>✅ اطلاعات بانکی شما تایید شده است. در صورت نیاز به تغییر، با پشتیبانی تماس بگیرید.</p>
    </div>

    <div v-else-if="form.status === 'rejected'" class="reject-box">
      <strong>❌ اطلاعات بانکی شما رد شده است.</strong>
      <p v-if="form.reject_reason"><strong>دلیل:</strong> {{ form.reject_reason }}</p>
      <p>لطفاً اطلاعات را اصلاح کرده و مجدداً ثبت کنید.</p>
    </div>

    <form @submit.prevent="submitForm" class="bank-form">
      <!-- ===== متن راهنما ===== -->
      <div class="info-box info-yellow">
        <p>⚠️ شماره موبایل و شماره شبا باید متعلق به یک کد ملی باشد.</p>
      </div>

      <!-- ===== عکس روی کارت ملی ===== -->
      <div class="form-group">
        <label class="required">عکس روی کارت ملی</label>
        <div class="file-upload-wrapper">
          <!-- ===== استفاده از label برای سازگاری با سافاری ===== -->
          <label
              for="frontInput"
              class="file-upload"
              :class="{ disabled: !canEdit }"
          >
            <span v-if="!form.card_image_front">📤 انتخاب عکس</span>
            <span v-else>✅ عکس انتخاب شد</span>
          </label>
          <input
              id="frontInput"
              ref="frontInput"
              type="file"
              accept="image/*"
              @change="handleFileUpload"
              :disabled="!canEdit"
              class="hidden-input"
          />
          <div v-if="form.card_image_front" class="preview">
            <img :src="form.card_image_front" alt="روی کارت ملی" />
          </div>
        </div>
        <small class="help-text">فایل با فرمت JPG یا PNG (حداکثر ۵ مگابایت)</small>
      </div>

      <!-- ===== کد ملی و تاریخ تولد ===== -->
      <div class="form-row">
        <div class="form-group half">
          <label class="required">کد ملی</label>
          <input
              v-model="form.national_code"
              type="text"
              placeholder="۱۰ رقم"
              class="form-control"
              :class="{ 'is-invalid': nationalCodeError }"
              maxlength="10"
              :disabled="!canEdit"
              @input="validateNationalCode"
          />
          <span v-if="nationalCodeError" class="error-text">{{ nationalCodeError }}</span>
        </div>

        <div class="form-group half">
          <label class="required">تاریخ تولد</label>
          <data-picker
              v-model="form.birth_date"
              :max="today"
              format="jYYYY/jMM/jDD"
              inputFormat="jYYYY/jMM/jDD"
              displayFormat="jYYYY/jMM/jDD"
              :disabled="!canEdit"
          />
          <small class="help-text">برای تطابق با کد ملی</small>
        </div>
      </div>

      <!-- ===== شماره شبا ===== -->
      <div class="form-group">
        <label class="required">شماره شبا</label>
        <input
            v-model="form.iban"
            type="text"
            placeholder="مثلاً IR123456789012345678901234"
            class="form-control"
            :class="{ 'is-invalid': ibanError }"
            maxlength="26"
            :disabled="!canEdit"
            @input="validateIban"
        />
        <span v-if="ibanError" class="error-text">{{ ibanError }}</span>
        <small class="help-text">شماره شبا باید 26 رقم باشد و با IR شروع شود.</small>
      </div>

      <!-- ===== پذیرش قوانین ===== -->
      <div class="form-group">
        <label class="required">پذیرش قوانین</label>
        <DepositTerms
            v-model="termsAccepted"
            type="marketOwner"
        />
        <small class="help-text">
          برای فعال‌سازی بیعانه، ابتدا باید قوانین و مقررات را بپذیرید.
        </small>
      </div>

      <!-- ===== دکمه‌ها ===== -->
      <div class="form-actions" v-if="canEdit">
        <button
            type="submit"
            class="btn-primary"
            :disabled="submitting"
        >
          {{ submitting ? '⏳ در حال ذخیره...' : (hasRecord ? '💾 ویرایش و ثبت مجدد' : '💾 ثبت اطلاعات') }}
        </button>
      </div>

      <div class="form-actions" v-else>
        <span class="info-text">
          <span v-if="form.status === 'pending'">⏳ در انتظار تایید مدیریت</span>
          <span v-else-if="form.status === 'approved'">✅ اطلاعات تایید شده است</span>
        </span>
      </div>
    </form>
  </div>
</template>

<script>
import axios from 'axios';
import DepositTerms from './DepositTerms.vue';

export default {
  name: 'BankAccount',

  components: {
    DepositTerms
  },

  data() {
    return {
      loading: false,
      submitting: false,
      termsAccepted: false,
      ibanError: '',
      nationalCodeError: '',
      today: new Date().toISOString().split('T')[0],
      hasRecord: false,
      rawFile: null,  // ✅ فایل اصلی برای ارسال به بک‌اند
      form: {
        id: null,
        card_image_front: null,
        national_code: '',
        birth_date: '',
        iban: '',
        status: null,
        reject_reason: null
      }
    };
  },

  computed: {
    canEdit() {
      if (!this.hasRecord) return true;
      if (this.form.status === 'rejected') return true;
      return false;
    },

    statusClass() {
      const map = {
        pending: 'status-pending',
        approved: 'status-approved',
        rejected: 'status-rejected'
      };
      return map[this.form.status] || 'status-empty';
    },

    statusLabel() {
      const map = {
        pending: '⏳ در انتظار تایید',
        approved: '✅ تایید شده',
        rejected: '❌ رد شده'
      };
      return map[this.form.status] || 'ثبت نشده';
    }
  },

  mounted() {
    this.loadBankAccount();
  },

  methods: {
    // ==========================================
    // بارگذاری اطلاعات
    // ==========================================
    async loadBankAccount() {
      this.loading = true;
      try {
        const { data } = await axios.get('/api/admin/bank-account',
            {
              headers:{
                'Authorization':'Bearer ' + localStorage.getItem('token')
              }
            });

        if (!data || !data.has_record) {
          this.hasRecord = false;
          this.rawFile = null;
          this.form = {
            id: null,
            card_image_front: null,
            national_code: '',
            birth_date: '',
            iban: '',
            status: null,
            reject_reason: null
          };
          return;
        }

        this.hasRecord = true;
        this.rawFile = null;
        this.form = {
          id: data.id,
          card_image_front: data.card_image_front || null,
          national_code: data.national_code || '',
          birth_date: data.birth_date || '',
          iban: data.iban || '',
          status: data.status || 'pending',
          reject_reason: data.reject_reason || null
        };

        if (data.status === 'rejected') {
          this.form.card_image_front = null;
        }

      } catch (error) {
        console.error('خطا در بارگذاری:', error);
      } finally {
        this.loading = false;
      }
    },

    // ==========================================
    // ✅ آپلود عکس (سازگار با سافاری)
    // ==========================================
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      // اعتبارسنجی نوع فایل
      if (!file.type.startsWith('image/')) {
        alert('لطفاً یک فایل تصویری انتخاب کنید.');
        return;
      }

      // اعتبارسنجی حجم
      if (file.size > 5 * 1024 * 1024) {
        alert('حجم فایل نباید بیشتر از ۵ مگابایت باشد.');
        return;
      }

      // ==========================================
      // ✅ استفاده از createObjectURL برای پیش‌نمایش
      // ==========================================
      if (this.form.card_image_front && this.form.card_image_front.startsWith('blob:')) {
        URL.revokeObjectURL(this.form.card_image_front);
      }

      this.rawFile = file;
      this.form.card_image_front = URL.createObjectURL(file);
    },

    // ==========================================
    // اعتبارسنجی کد ملی
    // ==========================================
    validateNationalCode() {
      const code = this.form.national_code || '';
      this.form.national_code = code.replace(/\D/g, '');

      if (!this.form.national_code) {
        this.nationalCodeError = '';
        return;
      }

      if (this.form.national_code.length !== 10) {
        this.nationalCodeError = 'کد ملی باید دقیقاً ۱۰ رقم باشد.';
        return;
      }

      this.nationalCodeError = '';
    },

    // ==========================================
    // اعتبارسنجی شبا
    // ==========================================
    validateIban() {
      const iban = this.form.iban || '';
      this.form.iban = iban.replace(/\s/g, '').toUpperCase();

      if (!this.form.iban) {
        this.ibanError = '';
        return;
      }

      if (!this.form.iban.startsWith('IR')) {
        this.ibanError = 'شماره شبا باید با IR شروع شود.';
        return;
      }

      if (this.form.iban.length !== 26) {
        this.ibanError = 'شماره شبا باید بدون IR دقیقاً ۲۴ رقم باشد.';
        return;
      }

      if (!/^[A-Z0-9]+$/.test(this.form.iban)) {
        this.ibanError = 'شماره شبا فقط شامل حروف و اعداد است.';
        return;
      }

      this.ibanError = '';
    },

    // ==========================================
    // ✅ تبدیل فایل به Base64 (سازگار با سافاری)
    // ==========================================
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },

    // ==========================================
    // ارسال فرم
    // ==========================================
    async submitForm() {
      // اعتبارسنجی
      if (!this.form.card_image_front) {
        alert('لطفاً عکس روی کارت ملی را انتخاب کنید.');
        return;
      }

      if (!this.form.national_code || this.form.national_code.length !== 10) {
        alert('کد ملی باید ۱۰ رقم باشد.');
        return;
      }

      if (!this.form.birth_date) {
        alert('لطفاً تاریخ تولد را وارد کنید.');
        return;
      }

      if (!this.form.iban || this.form.iban.length !== 26) {
        alert('شماره شبا باید بدون IR دقیقا ۲۴ رقم باشد.');
        return;
      }

      if (!this.termsAccepted) {
        alert('برای ثبت اطلاعات باید قوانین و مقررات را بخوانید و بپذیرید.');
        return;
      }

      this.submitting = true;
      try {
        // ==========================================
        // ✅ تبدیل فایل به Base64 قبل از ارسال
        // ==========================================
        let base64Image = this.form.card_image_front;

        if (this.rawFile) {
          base64Image = await this.fileToBase64(this.rawFile);
        }

        const payload = {
          card_image_front: base64Image,
          national_code: this.form.national_code,
          birth_date: this.form.birth_date,
          iban: this.form.iban
        };

        const url = '/api/admin/bank-account';
        const method = this.form.id ? 'put' : 'post';

        await axios[method](url, payload);

        this.$toast.success(this.hasRecord
            ? 'اطلاعات بانکی با موفقیت ویرایش و برای تایید ارسال شد.'
            : 'اطلاعات بانکی با موفقیت ثبت و برای تایید ارسال شد.');

        // بارگذاری مجدد
        this.loadBankAccount();

      } catch (error) {
        const msg = error.response?.data?.message || 'خطا در ذخیره اطلاعات';
        if (this.$toast) {
          this.$toast.error(msg);
        } else {
          alert(msg);
        }
        console.error(error);
      } finally {
        this.submitting = false;
      }
    }
  },

  // ==========================================
  // آزادسازی حافظه
  // ==========================================
  beforeDestroy() {
    if (this.form.card_image_front && this.form.card_image_front.startsWith('blob:')) {
      URL.revokeObjectURL(this.form.card_image_front);
    }
  }
};
</script>

<style scoped>
/* ========================================== */
/* استایل‌های اصلی */
/* ========================================== */
.bank-account {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f0f0f0;
}

.section-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 18px;
}

/* ========================================== */
/* بج وضعیت */
/* ========================================== */
.status-badge {
  display: inline-block;
  padding: 4px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
}

.status-empty { background: #f4f4f5; color: #909399; }
.status-pending { background: #fdf6ec; color: #e6a23c; }
.status-approved { background: #f0f9eb; color: #67c23a; }
.status-rejected { background: #fef0f0; color: #f56c6c; }

/* ========================================== */
/* باکس‌های اطلاع‌رسانی */
/* ========================================== */
.info-box {
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
}

.info-box p {
  margin: 0;
  font-size: 14px;
}

.info-blue { background: #ecf5ff; border: 1px solid #b3d8ff; }
.info-blue p { color: #409eff; }

.info-orange { background: #fdf6ec; border: 1px solid #f5dab1; }
.info-orange p { color: #e6a23c; }

.info-green { background: #f0f9eb; border: 1px solid #c2e7b0; }
.info-green p { color: #67c23a; }

.info-yellow { background: #fdf6ec; border: 1px solid #f5dab1; }
.info-yellow p { color: #e6a23c; }

.reject-box {
  background: #fef0f0;
  border: 1px solid #fde2e2;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
}

.reject-box strong {
  color: #f56c6c;
  display: block;
  margin-bottom: 4px;
}

.reject-box p {
  margin: 4px 0;
  color: #606266;
  font-size: 14px;
}

/* ========================================== */
/* فرم */
/* ========================================== */
.bank-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  font-weight: 600;
  font-size: 14px;
  color: #2c3e50;
}

.form-group label.required::after {
  content: '*';
  color: #f56c6c;
  margin-right: 4px;
}

.form-control {
  padding: 10px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s;
}

.form-control:focus {
  outline: none;
  border-color: #409eff;
  box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.1);
}

.form-control.is-invalid {
  border-color: #f56c6c;
}

.form-control:disabled {
  background: #f5f7fa;
  cursor: not-allowed;
  color: #909399;
}

.error-text {
  color: #f56c6c;
  font-size: 12px;
}

.help-text {
  color: #909399;
  font-size: 12px;
}

/* ========================================== */
/* آپلود فایل */
/* ========================================== */
.file-upload-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* ✅ input مخفی (سازگار با سافاری) */
.hidden-input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* ✅ label به عنوان دکمه کلیک‌پذیر */
.file-upload {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  background: #f5f7fa;
  border: 2px dashed #dcdfe6;
  border-radius: 6px;
  cursor: pointer;
  text-align: center;
  color: #606266;
  font-size: 14px;
  transition: all 0.3s;
  min-height: 44px;
  width: 100%;
  box-sizing: border-box;
}

.file-upload:hover:not(.disabled) {
  border-color: #409eff;
  background: #ecf5ff;
}

.file-upload.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.preview {
  margin-top: 6px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #ebeef5;
  max-width: 250px;
}

.preview img {
  width: 100%;
  height: auto;
  display: block;
}

/* ========================================== */
/* دکمه‌ها */
/* ========================================== */
.form-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
  margin-top: 8px;
}

.btn-primary {
  padding: 10px 28px;
  background: #409eff;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-primary:hover:not(:disabled) {
  background: #66b1ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
}

.btn-primary:disabled {
  background: #a0cfff;
  cursor: not-allowed;
}

.info-text {
  font-size: 14px;
  color: #909399;
}

/* ========================================== */
/* ریسپانسیو */
/* ========================================== */
@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .bank-account {
    padding: 16px;
  }

  .form-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .btn-primary {
    width: 100%;
    justify-content: center;
  }
}
</style>